import dayjs from 'dayjs';
import { PromotionTypeEnum, StandardPaymentType } from './Debt';

export interface GTMConfig {
  gtmId: string;
  gtmAuth: string;
  gtmPreview: string;
}

// **********************************************************************
// TAGS
// **********************************************************************
export interface GTMData {
  session_id: string;
  user_id: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  viewMode: string;
  cust_age_segment: string;
  cust_birth_year: string;
  cust_gender: string;
  cust_birth_country: string;
  cust_nationality: string;
  cust_marital_status: string;
  cust_has_email: string;
  cust_email_domain: string;
  cust_has_mobile_phone: string;
  cust_card_registered_count: string;
  cust_contract_type: string;
  cust_monthly_income: string;
  cust_status: string;
  unpaid_loans_count: string;
  unpaid_installments_count: string;
  unpaid_amount: string;
  unpaid_installment_max_age: string;
  unpaid_has_promotions: string;
  unpaid_max_order_number: string;
  unpaid_personal_loan_count: string;
  unpaid_consumer_loan_count: string;
  device_google_pay_capable: string;
  device_apple_pay_capable: string;
  payment_transaction_reason: string;
  payment_type: string;
  payment_final_amount: string;
  payment_promotion_used: string;
  payment_third_used: string;
  payment_result: string;
  payment_card_brand: string;
  payment_iin_number: string;
}

export interface GTMUserCargado {
  user_id: number;
}

export interface GTMSession {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  viewMode: string;
}

export interface GTMCustomer {
  cust_age_segment: string;
  cust_birth_year: string;
  cust_gender: string;
  cust_birth_country: string;
  cust_nationality: string;
  cust_marital_status: string;
  cust_has_email: string;
  cust_email_domain: string;
  cust_has_mobile_phone: string;
  cust_card_registered_count: string;
  cust_contract_type: string;
  cust_monthly_income: string;
  cust_status: string;
}

export interface GTMDebt {
  unpaid_loans_count: string;
  unpaid_installments_count: string;
  unpaid_amount: string;
  unpaid_installment_max_age: string;
  unpaid_has_promotions: string;
  unpaid_has_24h_promotion: string;
  unpaid_max_order_number: string;
  unpaid_personal_loan_count: string;
  unpaid_consumer_loan_count: string;
  allow_payment_plan_count: string;
  payment_plan_installments_paid_count: string;
  payment_plan_installments_unpaid_count: string;
  payment_plan_min_start_day: string;
  payment_plan_max_end_day: string;
}

export interface GTMDevice {
  device_google_pay_capable: string;
  device_apple_pay_capable: string;
}

export interface GTMPayment {
  payment_id: string;
  payment_transaction_reason: string;
  payment_type: string;
  payment_final_amount: string;
  payment_savings_amount: string;
  payment_promotion_used: string;
  payment_third_used: string;
  payment_result: string;
  payment_card_brand: string;
  payment_iin_number: string;
  payment_method: string;
}

// **********************************************************************
// EVENTS
// **********************************************************************

export enum GAnalyticsEventType {
  no_debt_entry = 'no_debt_entry',
  access_denied = 'access_denied',
  contact_us = 'contact_us',
  event_ko = 'event_ko',
  call_me_back = 'call_me_back',
  show_debt_details = 'show_debt_details',
  show_promotions = 'show_promotions',
  show_promotion_details = 'show_promotion_details',
  change_debt_type = 'change_debt_type',
  fastpay = 'fastpay',
  continue_debt_type = 'continue_debt_type',
  show_other_options = 'show_other_options',
  change_payment_method = 'change_payment_method',
  back_debt_type = 'back_debt_type',
  continue_payment_method = 'continue_payment_method',
  payment_error = 'payment_error',
  add_payment_info = 'add_payment_info',
  continue_payment = 'continue_payment',
  purchase = 'purchase',
  show_in_advance_payment = 'show_in_advance_payment',
  in_advance_payment = 'in_advance_payment',
  show_payment_plan_debt_details = 'show_payment_plan_debt_details',
  change_payment_option = 'change_payment_option',
  select_operation = 'select_operation',
  back_start = 'back_start',
  user_cargado = 'user_cargado',
}

export enum GTMFlowSteps {
  ENTRY = 'ENTRY',
  ENTRY_CO = 'ENTRY-CO',
  TYPES_PAYMENTS = 'TYPES-PAYMENTS',
  TPV = 'TPV',
  TRANSITION = 'TRANSITION',
  TIMEOUT = 'TIMEOUT',
  DENIAL = 'DENIAL',
  UNKNOWN_ERROR = 'UNKNOWN-ERROR',
  UNPROCESSABLE = 'UNPROCESSABLE',
  SUCCESS = 'SUCCESS',
  PAYMENT_CHECK = 'PAYMENT-CHECK',
  NO_DEBT = 'NO-DEBT'
}

export enum GTMPaymentOptionsEnum {
  // ACT
  PROMO_24H = 'PROMO24h', // Promotion type 24h - H24 
  PAYMENT_ADVANCE = 'PAYMENT&ADVANCE', // Pago adelantado - PAN
  INSTALLMENT = '1INSTALLMENT', // Pago de una cuota pendiente - DE1
  INSTALLMENTS = '2INSTALLMENTS', // Pagos de cuotas pendientes - Promoción - DEP
  DEBT_AMORTIZATION = 'DEBT_AMORTIZATION', // Cancela toda tu deuda - DET
  CANCELATION = 'CANCELATION', // Cancelación de deuda + ppal pte - POP 
  
  // CO
  PAYMENT_PLAN = 'PAYMENT_PLAN' // Creación o pago del plan de pagos - PPG, CPP 
  // DEBT_AMORTIZATION - DEC 
  // CANCELATION - PCO
}

export interface GTMStepNumber {
  step: GTMFlowSteps;
}

export interface GTMKoEvent {
  step: GTMStepNumber['step'];
}

export interface GTMCallBackEvent {
  step: GTMStepNumber['step'];
  phone: string;
  when: string;
}

export enum GTMCallBackPhone {
  registered = 'REGISTERED',
  other = 'OTHER',
}

export enum GTMCallBackWhen {
  asap = 'ASAP',
  scheduled = 'SCHEDULED',
}

export interface GTMTimeData {
  time_elapsed: number;
}

export interface GTMTimeElapse {
  initTime: dayjs.Dayjs;
  finishTime: dayjs.Dayjs;
}

export interface GTMContinuePaymentMethod {
  time_elapsed: number;
  paymentMethod: string;
}

export interface GTMPaymentInfo {
  currency: string;
  value: number;
  items: ItemsArray[];
}

export interface ItemsArray {
  itemId: string;
  itemName: string;
}

export interface GTMPaymentData {
  payment_method: string;
  payment_transaction_reason: string;
  payment_iin_number: string;
  payment_card_brand: string;
  payment_third_used: string;
}

export interface GTMContinuePayment {
  time_elapsed: number;
  step: GTMFlowSteps;
  payment_method: GTMPaymentData["payment_method"];
  payment_transaction_reason: GTMPaymentData["payment_transaction_reason"]
  payment_iin_number: GTMPaymentData["payment_iin_number"];
  payment_card_brand: GTMPaymentData["payment_card_brand"];
  payment_third_used: GTMPaymentData["payment_third_used"];
}

export interface GTMPurchaseData {
  currency: string;
  transaction_id: string;
  value: number;
  items: ItemsArray[];
}

export interface GTMChangePaymentOption {
  time_elapsed: number;
  payment_option: StandardPaymentType | PromotionTypeEnum;
}

export interface GTMPaymentPlanData {
  term_selected: number;
  day_selected: number;
  amount_selected: number;
}

export interface GTMFastPayData {
  time_elapsed: number;
  payment_option: StandardPaymentType | PromotionTypeEnum;
  payment_plan_data?: GTMPaymentPlanData;
}
